import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import type { ProductionRecipe } from '@/types/legacy';
import { Loader2, Plus, Trash2, Thermometer, Gauge, Zap, Clock, TrendingUp } from 'lucide-react';

interface RecipeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipe?: ProductionRecipe | null;
}

interface RampRow {
  target_temp: number | null;
  rate_deg_min: number | null;
  hold_minutes: number | null;
  pressure_bar: number | null;
}

const emptyRamp = (): RampRow => ({
  target_temp: null,
  rate_deg_min: null,
  hold_minutes: null,
  pressure_bar: null,
});

export function RecipeFormDialog({ open, onOpenChange, recipe }: RecipeFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [ramps, setRamps] = useState<RampRow[]>([emptyRamp()]);

  useEffect(() => {
    if (!open) return;

    setName(recipe?.name || '');
    setNotes(recipe?.notes || recipe?.description || '');
    setIsActive(recipe?.is_active ?? true);

    if (recipe?.id) {
      // Cargar rampas existentes
      (async () => {
        const { data, error } = await api
          .from('production_recipe_ramps')
          .select('*')
          .eq('recipe_id', recipe.id)
          .order('ramp_order', { ascending: true });
        if (!error && data && data.length > 0) {
          setRamps(
            data.map((r: any) => ({
              target_temp: r.target_temp,
              rate_deg_min: r.rate_deg_min,
              hold_minutes: r.hold_minutes,
              pressure_bar: r.pressure_bar,
            }))
          );
        } else {
          setRamps([emptyRamp()]);
        }
      })();
    } else {
      setRamps([emptyRamp()]);
    }
  }, [open, recipe]);

  const updateRamp = (index: number, field: keyof RampRow, value: number | null) => {
    setRamps((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRamp = () => setRamps((prev) => [...prev, emptyRamp()]);

  const removeRamp = (index: number) => {
    setRamps((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: 'Error', description: 'El nombre es requerido', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      // Derivar valores de cabecera de la primera rampa (para compatibilidad con lotes/plantillas)
      const firstRamp = ramps[0];
      const maxTemp = Math.max(...ramps.map((r) => r.target_temp || 0), 0);
      const headerPayload = {
        name: name.trim(),
        description: null,
        temperature_celsius: maxTemp || null,
        pressure_bar: firstRamp?.pressure_bar || null,
        curing_time_hours: null,
        ingredients: null,
        preparation_steps: null,
        is_active: isActive,
        notes: notes || null,
      };

      let recipeId = recipe?.id;

      if (recipe?.id) {
        const { error } = await api
          .from('production_recipes')
          .update(headerPayload)
          .eq('id', recipe.id);
        if (error) throw error;
      } else {
        const { data, error } = await api
          .from('production_recipes')
          .insert([headerPayload])
          .select()
          .single();
        if (error) throw error;
        recipeId = data.id;
      }

      // Sincronizar rampas: borrar las antiguas e insertar las nuevas
      if (recipeId) {
        await api.from('production_recipe_ramps').delete().eq('recipe_id', recipeId);

        const rampsToInsert = ramps
          .filter(
            (r) =>
              r.target_temp !== null ||
              r.rate_deg_min !== null ||
              r.hold_minutes !== null ||
              r.pressure_bar !== null
          )
          .map((r, idx) => ({
            recipe_id: recipeId,
            ramp_order: idx + 1,
            target_temp: r.target_temp,
            rate_deg_min: r.rate_deg_min,
            hold_minutes: r.hold_minutes,
            pressure_bar: r.pressure_bar,
          }));

        if (rampsToInsert.length > 0) {
          const { error: rampError } = await api
            .from('production_recipe_ramps')
            .insert(rampsToInsert);
          if (rampError) throw rampError;
        }
      }

      toast({
        title: recipe ? 'Receta actualizada' : 'Receta creada',
        description: 'La receta se guardo correctamente',
      });

      queryClient.invalidateQueries({ queryKey: ['production_recipes'] });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la receta',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{recipe ? 'Editar Receta' : 'Nueva Receta'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nombre */}
          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Receta Estandar A"
              required
            />
          </div>

          {/* Rampas */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Rampas de Curado
                </Label>
                <p className="text-sm text-muted-foreground">
                  Anade las rampas necesarias. Cada rampa con su temperatura, velocidad, tiempo y presion.
                </p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addRamp} className="gap-2">
                <Plus className="w-4 h-4" />
                Anadir Rampa
              </Button>
            </div>

            {ramps.map((ramp, index) => (
              <div key={index} className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    Rampa {index + 1}
                  </span>
                  {ramps.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeRamp(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1 text-xs">
                      <Thermometer className="w-3 h-3" />
                      Temperatura (C)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ramp.target_temp ?? ''}
                      onChange={(e) =>
                        updateRamp(index, 'target_temp', e.target.value ? parseFloat(e.target.value) : null)
                      }
                      placeholder="Ej: 180"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1 text-xs">
                      <Zap className="w-3 h-3" />
                      Velocidad (C/min)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ramp.rate_deg_min ?? ''}
                      onChange={(e) =>
                        updateRamp(index, 'rate_deg_min', e.target.value ? parseFloat(e.target.value) : null)
                      }
                      placeholder="Ej: 2.5"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1 text-xs">
                      <Clock className="w-3 h-3" />
                      Tiempo (min)
                    </Label>
                    <Input
                      type="number"
                      value={ramp.hold_minutes ?? ''}
                      onChange={(e) =>
                        updateRamp(index, 'hold_minutes', e.target.value ? parseInt(e.target.value) : null)
                      }
                      placeholder="Ej: 60"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1 text-xs">
                      <Gauge className="w-3 h-3" />
                      Presion (Bar)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ramp.pressure_bar ?? ''}
                      onChange={(e) =>
                        updateRamp(index, 'pressure_bar', e.target.value ? parseFloat(e.target.value) : null)
                      }
                      placeholder="Ej: 7"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas adicionales sobre la receta"
              rows={3}
            />
          </div>

          {/* Activa */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label className="text-base">Activa</Label>
              <div className="text-sm text-muted-foreground">
                La receta esta disponible para produccion
              </div>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {recipe ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
